import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Modal } from '@carouselgg/nexus/modal'
import { Button } from '@carouselgg/nexus/button'
import { Spinner } from '@carouselgg/nexus/spinner'
import { Text } from '@carouselgg/nexus/text'
import { toast } from '@carouselgg/nexus/toast'
import { ContactPicker } from '@carouselgg/nexus/contact-picker'
import { contactErrorsFromProblem, contactMethodLabel, type ContactChoice, type ContactPersonOption } from '@carouselgg/nexus/contact-input'
import { Check, X } from '@carouselgg/nexus/icon'
import { ApiError } from '@carouselgg/auth/client'
import { getTeamContactInfo, setRegistrationContact, type MyCompetition } from '../api/teams'
import styles from './LeagueContact.module.css'

export interface LeagueContactProps {
  competition: MyCompetition
  /** Called after the contact was changed, so the caller can re-read competitions. */
  onChanged: () => void
  /** Name the team in the line, when several teams of the viewer's share a competition. */
  showTeam?: boolean
}

/**
 * Who the league reaches about a team for one competition, with a way for the
 * team's managers to change it. Render only when `can_manage_contact`: the API
 * sends the contact to nobody else.
 */
export function LeagueContact({ competition: c, onChanged, showTeam }: LeagueContactProps) {
  const [editing, setEditing] = useState(false)
  const contact = c.contact
  return (
    <div className={styles.row}>
      <Text size="xs" muted>
        {showTeam && `${c.team_name}. `}
        {contact
          ? <>League contact: {contact.name} by {contactMethodLabel(contact.method)}</>
          : 'League contact: nobody reachable'}
        {' · '}
      </Text>
      <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Change</Button>
      {editing && (
        <ChangeContact
          competition={c}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged() }}
        />
      )}
    </div>
  )
}

function ChangeContact({ competition: c, onClose, onSaved }: {
  competition: MyCompetition
  onClose: () => void
  onSaved: () => void
}) {
  const [options, setOptions] = useState<ContactPersonOption[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [choice, setChoice] = useState<ContactChoice | null>(
    c.contact?.user_id ? { user_id: c.contact.user_id, method: c.contact.method } : null,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(false)
    getTeamContactInfo(c.team_org_slug, c.team_id)
      .then((info) => { if (!cancelled) setOptions(info.options) })
      .catch(() => { if (!cancelled) setLoadError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [c.team_org_slug, c.team_id, attempt])

  const save = async () => {
    if (!choice) { setError('Pick who the league should reach about this team'); return }
    setSaving(true)
    setError('')
    try {
      await setRegistrationContact(c.organization_id, c.season_id, c.registration_id, choice)
      toast.success(`The league will now reach ${c.team_name} through the contact you picked`)
      onSaved()
      return
    } catch (err) {
      const fieldErrors = err instanceof ApiError ? contactErrorsFromProblem(err.body) : null
      setError(Object.values(fieldErrors ?? {})[0] ?? (err instanceof Error ? err.message : 'Could not change the contact'))
    }
    setSaving(false)
  }

  return (
    <Modal
      open
      title={`League contact for ${c.team_name}`}
      onClose={onClose}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}><X size={16} /> Close</Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving || loading || loadError || !choice}>
            {saving ? <Spinner size="sm" /> : <><Check size={16} /> Save</>}
          </Button>
        </>
      )}
    >
      {loading && <Text size="sm" muted>Loading {c.team_name}&apos;s people…</Text>}
      {loadError && (
        <div role="alert">
          <Text size="sm">Couldn&apos;t load the team&apos;s contacts.</Text>
          <Button size="sm" variant="secondary" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
        </div>
      )}
      {!loading && !loadError && (
        <ContactPicker
          label="Contact for this event"
          description={`Pick who ${c.organization_name} should reach about ${c.team_name} in ${c.name}.`}
          options={options}
          value={choice}
          onChange={(next) => { setChoice(next); setError('') }}
          error={error}
          disabled={saving}
        />
      )}
      {!loading && !loadError && (
        <Text size="sm" muted>
          Not listed with a way to be reached?{' '}
          <Link to="/settings/sign-in#team-contact">Add one in your account</Link>
        </Text>
      )}
    </Modal>
  )
}
